import { Fragment, useRef, useState } from "react";
import { bool, string } from "prop-types";
import cx from "classnames";
import FlagPlainIcon from "../Icon/FlagPlainIcon.jsx";
import Popup from "../../../../Common/js/components/Popup/Popup.jsx";
import { useAnnotationContext } from "./AnnotationContext.jsx";
import { highlightTypes } from "./annotationCommonPropTypes.js";
import { useFauxSelection } from "./useFauxSelection.jsx";
import useBoundingClientRect from "../../common/useBoundingClientRect.js";

const propTypes = {
  width: string.isRequired,
  height: string.isRequired,
  highlightType: highlightTypes,
  isAnnotated: bool.isRequired,
};

const SquareAnnotationMarker = (props) => {
  const { width, height, highlightType, isAnnotated } = props;

  const {
    data: {
      content: { viewAddAnnotationText, createNewAnnotationText },
    },
    marker: { markerPositionTop, markerPositionRight, markerNoColor },
    state: { isModalOpen, isOverlayActive, isSelectionActive },
    actions: {
      activateSelection,
      deactivateSelection,
      activateOverlay,
      deactivateOverlay,
      openModalWithSelection,
      openModalWithOverlay,
      closeModal,
    },
  } = useAnnotationContext();

  // Get a ref to the marker, so we can affect the non-React DOM
  const annotationMarkerRef = useRef(null);

  // get the button width so we can place the popup
  const [{ width: buttonWidth }, buttonRef] = useBoundingClientRect();

  // Component-level state for the tooltip, only triggered by this component
  const [tooltipHidden, setTooltipHidden] = useState(true);

  // Create a faux selection state for the content to be annotated
  useFauxSelection(isSelectionActive, annotationMarkerRef);

  // Event handlers
  const handleMouseEnter = (event) => {
    setTooltipHidden(false);
    if (highlightType === "selection") {
      activateSelection();
    } else {
      activateOverlay();
    }
  };
  const handleMouseLeave = (event) => {
    setTooltipHidden(true);
    if (!isModalOpen) {
      if (highlightType === "selection") {
        deactivateSelection();
      } else {
        deactivateOverlay();
      }
    }
  };
  const handleClick = (event) => {
    if (!isModalOpen) {
      if (highlightType === "selection") {
        openModalWithSelection();
      } else {
        openModalWithOverlay();
      }
    } else {
      closeModal();
    }
  };

  // Tooltip
  const annotationTooltipContent = isAnnotated
    ? viewAddAnnotationText
    : createNewAnnotationText;

  const annotationTooltipWidth = isAnnotated ? 156 : 160;
  const hasMarkerPositionTop = markerPositionTop ? true : false;
  const hasMarkerPositionRight = markerPositionRight ? true : false;
  const hasNoColor = markerNoColor ? true : false;

  return (
    <Fragment>
      <div
        className={cx("annotation-overlay", {
          "annotation-overlay--active": isOverlayActive,
        })}
      ></div>
      <div
        ref={annotationMarkerRef}
        className={cx("annotation-marker", "annotation-marker--square", {
          "annotation-marker--no-color": hasNoColor,
          "has-annotation": isAnnotated,
        })}
      >
        <button
          type="button"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
          style={{
            top: hasMarkerPositionTop ? markerPositionTop + "px" : "none",
            right: hasMarkerPositionRight ? markerPositionRight + "px" : "none",
          }}
          ref={buttonRef}
        >
          <FlagPlainIcon
            title={annotationTooltipContent}
            width={width}
            height={height}
          />

          {buttonWidth && (
            <Popup
              isHidden={tooltipHidden}
              left={buttonWidth / 2}
              top={0}
              offset={buttonWidth / 2 + 8}
              triangleAlignment={0}
              triangleSide="left"
              triangleSize={0} // we're hiding the triangle via css
              flipToContain="viewport"
              nudgeToContain="viewport"
              width={annotationTooltipWidth}
              className="annotation-marker__popup"
            >
              <span>{annotationTooltipContent}</span>
            </Popup>
          )}
        </button>
      </div>
    </Fragment>
  );
};

SquareAnnotationMarker.propTypes = propTypes;
export default SquareAnnotationMarker;
